import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { initGPS } from './gps';
import './App.css';

import Login from './pages/Login';
import ChangePIN from './pages/ChangePIN';
import AgentDashboard from './pages/agent/Dashboard';
import AgentMerchants from './pages/agent/Merchants';
import AgentFollowups from './pages/agent/Followups';
import AgentProfile from './pages/agent/Profile';
import EditMerchant from './pages/agent/EditMerchant';
import AgentRecoveryDashboard from './pages/agent/recovery/Dashboard';
import AgentRecoveryCases from './pages/agent/recovery/Cases';
import AgentRecoveryCaseDetail from './pages/agent/recovery/CaseDetail';
import MyPerformance from './pages/agent/MyPerformance';
import OwnerDashboard from './pages/owner/Dashboard';
import OwnerMerchants from './pages/owner/Merchants';
import OwnerFollowups from './pages/owner/Followups';
import OwnerServices from './pages/owner/Services';
import OwnerTeam from './pages/owner/Team';
import OwnerReports from './pages/owner/Reports';
import OwnerTeamActivity from './pages/owner/TeamActivity';
import OwnerTeam360 from './pages/owner/Team360';
import OwnerEditMerchant from './pages/owner/EditMerchant';
import OwnerCatalog from './pages/owner/Catalog';
import RecoveryDashboard from './pages/owner/recovery/Dashboard';
import RecoveryCustomers from './pages/owner/recovery/Customers';
import CustomerProfile from './pages/owner/recovery/CustomerProfile';
import RecoveryLoans from './pages/owner/recovery/Loans';
import RecoveryCases from './pages/owner/recovery/Cases';
import RecoveryCaseDetail from './pages/owner/recovery/CaseDetail';
import RecoveryActivity from './pages/owner/recovery/Activity';
import AdminPanel from './pages/admin/AdminPanel';

// Silent GPS init on app load
initGPS();

function getUser() {
  try {
    return JSON.parse(localStorage.getItem('au_user'));
  } catch {
    return null;
  }
}

function ProtectedRoute({ children, requiredRole, requiredRoles }) {
  const user = getUser();
  const token = localStorage.getItem('au_token');
  if (!token || !user) return <Navigate to="/login" replace />;
  const allowed = requiredRoles ? requiredRoles : (requiredRole ? [requiredRole] : null);
  if (allowed && !allowed.includes(user.role)) {
    return <Navigate to={user.role === 'owner' ? '/owner/dashboard' : '/dashboard'} replace />;
  }
  return children;
}

function RootRedirect() {
  const user = getUser();
  const token = localStorage.getItem('au_token');
  if (!token || !user) return <Navigate to="/login" replace />;
  if (user.mustChangePIN) return <Navigate to="/change-pin" replace />;
  return <Navigate to={user.role === 'owner' ? '/owner/dashboard' : '/dashboard'} replace />;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/login" element={<Login />} />
        <Route path="/change-pin" element={<ProtectedRoute><ChangePIN /></ProtectedRoute>} />

        {/* Agent Routes — also accessible to managers and admins for personal field capture */}
        <Route path="/dashboard" element={<ProtectedRoute requiredRoles={['agent', 'manager', 'admin']}><AgentDashboard /></ProtectedRoute>} />
        <Route path="/merchants" element={<ProtectedRoute requiredRoles={['agent', 'manager', 'admin']}><AgentMerchants /></ProtectedRoute>} />
        <Route path="/merchants/:id/edit" element={<ProtectedRoute requiredRoles={['agent', 'manager', 'admin']}><EditMerchant /></ProtectedRoute>} />
        <Route path="/followups" element={<ProtectedRoute requiredRoles={['agent', 'manager', 'admin']}><AgentFollowups /></ProtectedRoute>} />
        <Route path="/recovery" element={<ProtectedRoute requiredRoles={['agent', 'manager', 'admin']}><AgentRecoveryDashboard /></ProtectedRoute>} />
        <Route path="/recovery/cases" element={<ProtectedRoute requiredRoles={['agent', 'manager', 'admin']}><AgentRecoveryCases /></ProtectedRoute>} />
        <Route path="/recovery/cases/:loanAccountNumber" element={<ProtectedRoute requiredRoles={['agent', 'manager', 'admin']}><AgentRecoveryCaseDetail /></ProtectedRoute>} />
        <Route path="/my-performance" element={<ProtectedRoute requiredRoles={['owner', 'agent', 'manager', 'admin']}><MyPerformance /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute requiredRoles={['agent', 'manager', 'admin']}><AgentProfile /></ProtectedRoute>} />

        {/* Owner Routes */}
        <Route path="/owner/dashboard" element={<ProtectedRoute requiredRole="owner"><OwnerDashboard /></ProtectedRoute>} />
        <Route path="/owner/merchants" element={<ProtectedRoute requiredRole="owner"><OwnerMerchants /></ProtectedRoute>} />
        <Route path="/owner/merchants/:id/edit" element={<ProtectedRoute requiredRole="owner"><OwnerEditMerchant /></ProtectedRoute>} />
        <Route path="/owner/followups" element={<ProtectedRoute requiredRole="owner"><OwnerFollowups /></ProtectedRoute>} />
        <Route path="/owner/team-activity" element={<ProtectedRoute requiredRoles={['owner', 'manager', 'admin']}><OwnerTeamActivity /></ProtectedRoute>} />
        <Route path="/owner/team-360" element={<ProtectedRoute requiredRoles={['owner', 'manager', 'admin']}><OwnerTeam360 /></ProtectedRoute>} />
        <Route path="/owner/services" element={<ProtectedRoute requiredRole="owner"><OwnerServices /></ProtectedRoute>} />
        <Route path="/owner/catalog" element={<ProtectedRoute requiredRole="owner"><OwnerCatalog /></ProtectedRoute>} />
        <Route path="/owner/team" element={<ProtectedRoute requiredRoles={['owner', 'manager', 'admin']}><OwnerTeam /></ProtectedRoute>} />
        <Route path="/owner/reports" element={<ProtectedRoute requiredRoles={['owner', 'manager', 'admin']}><OwnerReports /></ProtectedRoute>} />

        {/* Recovery Routes (Owner) */}
        <Route path="/owner/recovery" element={<ProtectedRoute requiredRoles={['owner', 'manager', 'admin']}><RecoveryDashboard /></ProtectedRoute>} />
        <Route path="/owner/recovery/customers" element={<ProtectedRoute requiredRoles={['owner', 'manager', 'admin']}><RecoveryCustomers /></ProtectedRoute>} />
        <Route path="/owner/recovery/customers/:clientId" element={<ProtectedRoute requiredRoles={['owner', 'manager', 'admin']}><CustomerProfile /></ProtectedRoute>} />
        <Route path="/owner/recovery/loans" element={<ProtectedRoute requiredRoles={['owner', 'manager', 'admin']}><RecoveryLoans /></ProtectedRoute>} />
        <Route path="/owner/recovery/cases" element={<ProtectedRoute requiredRoles={['owner', 'manager', 'admin']}><RecoveryCases /></ProtectedRoute>} />
        <Route path="/owner/recovery/cases/:loanAccountNumber" element={<ProtectedRoute requiredRoles={['owner', 'manager', 'admin']}><RecoveryCaseDetail /></ProtectedRoute>} />
        <Route path="/owner/recovery/activity" element={<ProtectedRoute requiredRoles={['owner', 'manager', 'admin']}><RecoveryActivity /></ProtectedRoute>} />
        {/* Legacy /collections URL — kept as a deep link into the unified Activity report, filtered to payments */}
        <Route path="/owner/recovery/collections" element={<Navigate to="/owner/recovery/activity?type=payment" replace />} />

        {/* Manager URL aliases — Field Mode is default; Branch portal at /owner/recovery */}
        <Route path="/manager" element={<Navigate to="/dashboard" replace />} />
        <Route path="/manager/dashboard" element={<Navigate to="/dashboard" replace />} />

        {/* Admin Panel */}
        <Route path="/admin" element={<ProtectedRoute requiredRoles={['admin', 'owner']}><AdminPanel /></ProtectedRoute>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
